
import { cloneDeep } from '@apollo/client/utilities';
import _ from 'underscore';

import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';

import { ApiTokenResponse, callCreateApiTokenForScimConfiguration } from '../serverApi/requests/CreateScimConfigurationApiTokenRequest';
import { callCreateNewScimConfigurationRequest  } from '../serverApi/requests/CreateScimConfigurationRequest';
import { callDeleteApiTokenForScimConfiguration } from '../serverApi/requests/DeleteScimConfigurationApiTokenRequest';
import { callDeleteScimConfigurationRequest } from '../serverApi/requests/DeleteScimConfigurationRequest';
import { callGetAllScimConfigurationsRequest, IGetScimConfigurations } from '../serverApi/requests/GetAllScimConfigurationsRequest';
import { callUpdateScimConfigurationRequest } from '../serverApi/requests/UpdateScimConfigurationRequest';

export interface IScimConfiguration {
  name?: string;
  idpConfigurationId?: string;
  id?: string;
  isOwner?: boolean;
  createdAt?: string;
  isApiTokenActive?: boolean;
  authConfigName?: string;
  type?: string;
}

export interface IApiTokenRequest {
  scimConfigId?: string;
}

export interface ICreateUpdateScimConfigurationResponse extends IResult {
  scimConfiguration: IScimConfiguration;
}

export interface ICreateUpdateScimConfigurationRequest {
  scimConfiguration: {
    name?: string;
    idpConfigurationId?: string;
    authType?: string;
  };
}

export namespace ScimConfigurationsService {

  let siteId: string;
  let scimConfigurationsPromise: Promise<IScimConfiguration[]>;

  export function getAllScimConfigurations(refresh: boolean = false): Promise<IScimConfiguration[]> {

    checkIfAppStateChanged();
    if (!scimConfigurationsPromise || refresh) {
      scimConfigurationsPromise = callGetAllScimConfigurationsRequest()
        .then((result: IGetScimConfigurations) => {
          const scimConfigurations: IScimConfiguration[] = result?.scimConfigurations?.scimConfiguration || [];
          return scimConfigurations;
        })
        .catch((error) => {
          throw error;
        });
    }
    return scimConfigurationsPromise;
  }

  export function createNewScimConfiguration(scimConfig: IScimConfiguration): Promise<IScimConfiguration> {
    return callCreateNewScimConfigurationRequest(scimConfig)
      .then((result: ICreateUpdateScimConfigurationResponse) => {
        return result.scimConfiguration;
      })
      .catch((error) => {
        throw error;
      });
  }

  export function deleteScimConfiguration(scimConfigToDelete: IScimConfiguration): Promise<void> {
    return getAllScimConfigurations(false).then((configurations: IScimConfiguration[]) => {
      const configRef = _.find(configurations, (config: IScimConfiguration) => config.id === scimConfigToDelete.id);
      let newConfigurations = cloneDeep(configurations);

      newConfigurations = newConfigurations.filter((config: IScimConfiguration) => config.id !== scimConfigToDelete.id);
      return callDeleteScimConfigurationRequest(configRef)
        .then((result: IResult) => {
          scimConfigurationsPromise = Promise.resolve(newConfigurations);
        })
        .catch((error) => {
          throw error;
        });
    });
  }

  export function updateScimConfiguration(scimConfig: IScimConfiguration): Promise<IScimConfiguration> {
    return callUpdateScimConfigurationRequest(scimConfig)
      .then((result: ICreateUpdateScimConfigurationResponse) => {
        return result.scimConfiguration;
      })
      .catch((error) => {
        throw error;
      });
  }

  export function createApiTokenForScimConfiguration(request: IApiTokenRequest): Promise<string>{
    return callCreateApiTokenForScimConfiguration(request)
    .then((result: ApiTokenResponse) => {
      return result.apiToken.secret;
    })
    .catch((error) => {
      throw error;
    });
  }

  export function deleteApiTokenForScimConfiguration(scimConfigId: string): Promise<IResult>{
    return callDeleteApiTokenForScimConfiguration(scimConfigId)
    .then((result: IResult) => {
      return result;
    })
    .catch((error) => {
      throw error;
    });
  }

  function checkIfAppStateChanged() {
    const state = getAppStore().getState();
    const sessionSiteId = state && state.session && state.session.info && state.session.info.site.id;
    if (siteId !== sessionSiteId) {
      siteId = sessionSiteId;
      scimConfigurationsPromise = null;
    }
  }
}